import type { CreativePreview } from '@/features/campaigns/types/campaign';
import { ContentPreviewMedia } from '@/features/campaigns/components/ContentPreviewMedia';
import type { PreviewDeviceKind } from './constants';

type MediaObjectFit = 'contain' | 'cover';

type ExactScreenFrameProps = {
  device: PreviewDeviceKind;
  creative?: CreativePreview;
  detecting?: boolean;
  /** contain = native match; cover = stretch/crop like cross-orientation playback */
  objectFit?: MediaObjectFit;
  className?: string;
};

function mediaFitClass(fit: MediaObjectFit) {
  return fit === 'cover'
    ? 'h-full w-full object-cover'
    : 'h-full w-full object-contain';
}

/**
 * Screenista Signature Ambient Wallpaper (rendered in pure SVG/CSS)
 * Matches the official physical screen wallpaper:
 * Deep midnight-blue background, neon purple & cyan sweeping aurora light waves,
 * and the vibrant gradient SCREENiSTA play emblem with clean typography.
 */
function ScreenistaAmbientWallpaper({ prompt }: { prompt?: string }) {
  return (
    <div className="relative flex h-full w-full select-none flex-col items-center justify-center overflow-hidden bg-[#020b22]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,#0d2f78_0%,#041238_50%,#010618_100%)] opacity-90" />

      <svg
        className="pointer-events-none absolute inset-0 h-full w-full opacity-75"
        preserveAspectRatio="none"
        viewBox="0 0 800 600"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="waveCyan" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#2979FF" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#7C4DFF" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="wavePurple" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#E040FB" stopOpacity="0.7" />
            <stop offset="50%" stopColor="#7C4DFF" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#00E5FF" stopOpacity="0" />
          </linearGradient>
          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        <path
          d="M -100 500 C 150 400, 300 150, 900 200"
          stroke="url(#waveCyan)"
          strokeWidth="3.5"
          filter="url(#glow)"
        />
        <path
          d="M -50 550 C 250 480, 450 320, 850 40"
          stroke="url(#wavePurple)"
          strokeWidth="2.5"
          filter="url(#glow)"
        />
        <path
          d="M 50 600 C 350 420, 600 380, 900 100"
          stroke="#00E5FF"
          strokeWidth="1.2"
          strokeOpacity="0.6"
        />
        <path
          d="M -100 350 C 100 480, 400 520, 900 320"
          stroke="#D500F9"
          strokeWidth="1.5"
          strokeOpacity="0.5"
        />
      </svg>

      <div className="relative z-10 flex flex-col items-center justify-center gap-2.5 px-4 text-center">
        <div className="relative flex h-14 w-14 items-center justify-center drop-shadow-[0_0_20px_rgba(0,229,255,0.45)] sm:h-16 sm:w-16">
          <img
            src="/favicon.svg"
            alt=""
            className="h-full w-full object-contain"
          />
        </div>

        <div className="tracking-[0.16em] text-white">
          <span className="text-base font-black uppercase sm:text-lg">
            SCREEN<span className="text-[#00E5FF]">i</span>STA
          </span>
        </div>

        <div className="mt-1 flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-2.5 py-1 backdrop-blur-md">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#00E5FF]" />
          <span className="text-[10px] font-medium tracking-wide text-white/90">
            {prompt || 'Live preview standby'}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Landscape Display (Exact Wall-Mounted 16:9 Screen)
 */
function ScreenistaLandscapeDisplay({
  creative,
  detecting,
  objectFit,
}: {
  creative?: CreativePreview;
  detecting?: boolean;
  objectFit: MediaObjectFit;
}) {
  const hasCreative = Boolean(creative?.url);

  return (
    <div className="relative mx-auto flex h-full max-h-full w-full min-h-0 items-center justify-center p-2">
      <div className="relative flex w-full max-w-[440px] max-h-full aspect-[16/9] flex-col rounded-[12px] bg-gradient-to-b from-[#24272f] via-[#16181d] to-[#0c0d10] p-[6px] drop-shadow-xl">
        <div className="relative flex flex-1 min-h-0 flex-col overflow-hidden rounded-[7px] bg-[#000000] p-[3px]">
          <div className="relative h-full w-full overflow-hidden rounded-[4px] bg-[#020b22]">
            {detecting ? (
              <div className="relative flex h-full w-full flex-col items-center justify-center gap-2 bg-[#050a18] text-white">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-[#00E5FF]" />
                <span className="text-[11px] font-medium text-white/80">
                  Analyzing orientation…
                </span>
                <div className="pointer-events-none absolute inset-x-0 h-[2px] animate-pulse bg-gradient-to-r from-transparent via-[#00E5FF] to-transparent shadow-[0_0_8px_#00E5FF]" />
              </div>
            ) : hasCreative && creative ? (
              <ContentPreviewMedia
                url={creative.url}
                type={creative.type}
                className={mediaFitClass(objectFit)}
              />
            ) : (
              <ScreenistaAmbientWallpaper prompt="Upload landscape creative" />
            )}

            <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.02] to-white/[0.06]" />
          </div>
        </div>

        <div className="relative -mb-[3px] mt-[3px] flex shrink-0 items-center justify-center">
          <div className="flex items-center gap-1.5 rounded-b-[4px] border border-t-0 border-white/10 bg-[#0e1014] px-2.5 py-[2px] shadow-sm">
            <img
              src="/favicon.svg"
              alt=""
              className="h-3 w-3 object-contain"
            />
            <span className="text-[8px] font-black tracking-[0.16em] text-white/90 uppercase">
              SCREENiSTA
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Portrait Display (Exact Wall-Mounted 9:16 Screen)
 */
function ScreenistaPortraitDisplay({
  creative,
  detecting,
  objectFit,
}: {
  creative?: CreativePreview;
  detecting?: boolean;
  objectFit: MediaObjectFit;
}) {
  const hasCreative = Boolean(creative?.url);

  return (
    <div className="relative mx-auto flex h-full max-h-full w-full min-h-0 items-center justify-center">
      <div className="relative flex h-full max-h-full aspect-[9/16] w-auto max-w-full flex-col rounded-[14px] bg-gradient-to-b from-[#24272f] via-[#16181d] to-[#0b0c0f] p-[5px] ">
        <div className="relative flex flex-1 min-h-0 flex-col overflow-hidden rounded-[9px] bg-black p-[2.5px]">
          <div className="relative h-full w-full overflow-hidden rounded-[6px] bg-[#020b22]">
            {detecting ? (
              <div className="relative flex h-full w-full flex-col items-center justify-center gap-2 bg-[#050a18] text-white">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-[#00E5FF]" />
                <span className="text-[11px] font-medium text-white/80">
                  Analyzing orientation…
                </span>
                <div className="pointer-events-none absolute inset-x-0 h-[2px] animate-pulse bg-gradient-to-r from-transparent via-[#00E5FF] to-transparent shadow-[0_0_8px_#00E5FF]" />
              </div>
            ) : hasCreative && creative ? (
              <ContentPreviewMedia
                url={creative.url}
                type={creative.type}
                className={mediaFitClass(objectFit)}
              />
            ) : (
              <ScreenistaAmbientWallpaper prompt="Upload portrait creative" />
            )}

            <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.02] to-white/[0.06]" />
          </div>
        </div>

        <div className="mt-1 flex shrink-0 items-center justify-center gap-1 pb-0.5">
          <img
            src="/favicon.svg"
            alt=""
            className="h-2.5 w-2.5 object-contain opacity-80"
          />
          <span className="text-[7.5px] font-bold tracking-[0.16em] text-white/90 uppercase">
            SCREENiSTA
          </span>
        </div>
      </div>
    </div>
  );
}

export function ExactScreenFrame({
  device,
  creative,
  detecting,
  objectFit = 'contain',
  className = '',
}: ExactScreenFrameProps) {
  return (
    <div
      className={`relative flex h-full max-h-full w-full min-h-0 items-center justify-center overflow-hidden ${className}`}
    >
      {device === 'landscape' ? (
        <ScreenistaLandscapeDisplay
          creative={creative}
          detecting={detecting}
          objectFit={objectFit}
        />
      ) : (
        <ScreenistaPortraitDisplay
          creative={creative}
          detecting={detecting}
          objectFit={objectFit}
        />
      )}
    </div>
  );
}
